import { useRef, useEffect, useState, useCallback, memo } from 'react'
import { Box } from '@mui/material'
import InputCustom from './InputCustom'
import { useDebouncedCallback } from '../../../hooks'

const parseAddressComponents = (components) => {
	if (!components) return {}

	const parsed = {}
	components.forEach((component) => {
		const types = component.types
		if(!types) return
		if (types.includes('street_number')) {
			parsed.streetNumber = component.longText || component.shortText
		}
		if (types.includes('route')) {
			parsed.street = component.longText || component.shortText
		}
		if (types.includes('locality')) {
			parsed.city = component.longText || component.shortText
		}
		if (types.includes('administrative_area_level_1')) {
			parsed.state = component.shortText
			parsed.stateLong = component.longText
		}
		if (types.includes('country')) {
			parsed.country = component.shortText
			parsed.countryLong = component.longText
		}
		if (types.includes('postal_code')) {
			parsed.zipCode = component.longText || component.shortText
		}
	})
	return parsed
}

const addGlobalStyles = () => {
	if (document.getElementById('google-places-styles')) {
		return
	}

	const style = document.createElement('style')
	style.id = 'google-places-styles'
	style.textContent = `
		.address-suggestions {
			position: absolute;
			top: 100%;
			left: 0;
			right: 0;
			background: white;
			border: 1px solid #d1d5db;
			border-top: none;
			border-radius: 0 0 8px 8px;
			box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
			max-height: 100px;
			overflow-y: auto;
			z-index: 9999;
			margin-top: 2px;
		}

		.address-suggestion-item {
			padding: 5px 16px;
			cursor: pointer;
			border-bottom: 1px solid #f3f4f6;
			font-size: 14px;
			color: #111827;
			display: flex;
			align-items: flex-start;
			gap: 10px;
			transition: background-color 0.15s ease;
		}

		.address-suggestion-item:hover {
			background-color: #f9fafb;
		}

		.address-suggestion-item:last-child {
			border-bottom: none;
		}

		.address-suggestion-icon {
			flex-shrink: 0;
			width: 20px;
			height: 20px;
			margin-top: 2px;
			color: #6b7280;
		}

		.address-suggestion-content {
			flex: 1;
			min-width: 0;
		}

		.address-suggestion-main {
			font-weight: 500;
			margin-bottom: 2px;
			color: #111827;
		}

		.address-suggestion-secondary {
			font-size: 12px;
			color: #6b7280;
			line-height: 1.4;
		}

		.no-suggestions {
			padding: 12px 16px;
			text-align: center;
			color: #6b7280;
			font-size: 14px;
		}

		.loading-suggestions {
			padding: 12px 16px;
			text-align: center;
			color: #6b7280;
			font-size: 14px;
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 8px;
		}

		@keyframes pulse {
			0%, 100% {
				opacity: 1;
			}
			50% {
				opacity: 0.5;
			}
		}

		.loading-suggestions .dot {
			width: 6px;
			height: 6px;
			background-color: #6b7280;
			border-radius: 50%;
			animation: pulse 1.4s infinite;
		}

		.loading-suggestions .dot:nth-child(2) {
			animation-delay: 0.2s;
		}

		.loading-suggestions .dot:nth-child(3) {
			animation-delay: 0.4s;
		}
	`
	document.head.appendChild(style)
}

const GoogleAddressInput = memo(
	({
		value,
		name,
		placeholder = 'Enter address',
		required = false,
		autoFocus = false,
		className = '',
		disabled = false,
		label,
		'data_cy': data_cy,
		setDataFunction,
		data,
		onPlaceSelected,
		debounceTime = 500,
		minChars = 2,
		showLabel = false,
		...props
	}) => {
		const [suggestions, setSuggestions] = useState([])
		const [showSuggestions, setShowSuggestions] = useState(false)
		const [isLoading, setIsLoading] = useState(false)
		const [loadError, setLoadError] = useState(null)
		const containerRef = useRef(null)
		const sessionTokenRef = useRef(null)
		const isMounted = useRef(true)
		const isTyping = useRef(false)
		const lastSearchValue = useRef('')

		const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY

		useEffect(() => {
			isMounted.current = true
			addGlobalStyles()

			sessionTokenRef.current = Math.random().toString(36).substring(7)

			const handleClickOutside = (event) => {
				if (
					containerRef.current &&
					!containerRef.current.contains(event.target)
				) {
					setShowSuggestions(false)
				}
			}

			document.addEventListener('mousedown', handleClickOutside)

			return () => {
				isMounted.current = false
				document.removeEventListener('mousedown', handleClickOutside)
			}
		}, [])

		const fetchAutocompleteSuggestions = useCallback(
			async (input) => {
				if (!input || input.length < minChars) {
					setSuggestions([])
					setShowSuggestions(false)
					setIsLoading(false)
					return
				}

				if (input === lastSearchValue.current) {
					return
				}

				lastSearchValue.current = input

				if (!apiKey) {
					setLoadError('Google Maps API key is not configured')
					setIsLoading(false)
					return
				}

				try {
					const response = await fetch(
						`https://places.googleapis.com/v1/places:autocomplete`,
						{
							method: 'POST',
							headers: {
								'Content-Type': 'application/json',
								'X-Goog-Api-Key': apiKey,
							},
							body: JSON.stringify({
								input: input,
								sessionToken: sessionTokenRef.current,
								languageCode: 'en',
							}),
						}
					)

					if (!response.ok) {
						throw new Error(`API error: ${response.status}`)
					}

					const data = await response.json()

					if (isMounted.current) {
						if (data.suggestions && data.suggestions.length > 0) {
							setSuggestions(data.suggestions)
							setShowSuggestions(true)
						} else {
							setSuggestions([])
							setShowSuggestions(false)
						}
						setIsLoading(false)
					}
				} catch (error) {
					console.error(
						'Error fetching autocomplete suggestions:',
						error
					)
					if (isMounted.current) {
						setLoadError('Failed to load address suggestions')
						setIsLoading(false)
						setSuggestions([])
					}
				}
			},
			[apiKey, minChars]
		)

		const debouncedFetchSuggestions = useDebouncedCallback((input) => {
			isTyping.current = false
			fetchAutocompleteSuggestions(input)
		}, debounceTime)

		const handleInputChange = useCallback(
			(input) => {
				isTyping.current = true

				if (input && input.length >= minChars) {
					setIsLoading(true)
					setShowSuggestions(true)
				} else {
					setIsLoading(false)
					setShowSuggestions(false)
					setSuggestions([])
				}

				debouncedFetchSuggestions(input)
			},
			[debouncedFetchSuggestions, minChars]
		)

		const fetchPlaceDetails = useCallback(
			async (placeId) => {
				if (!apiKey) return

				setIsLoading(true)

				try {
					const response = await fetch(
						`https://places.googleapis.com/v1/places/${placeId}`,
						{
							method: 'GET',
							headers: {
								'Content-Type': 'application/json',
								'X-Goog-Api-Key': apiKey,
								'X-Goog-FieldMask':
									'id,displayName,formattedAddress,addressComponents',
							},
						}
					)

					if (!response.ok) {
						throw new Error(`API error: ${response.status}`)
					}

					const placeData = await response.json()

					if (isMounted.current) {
						const formattedAddress =
							placeData.formattedAddress ||
							placeData.displayName?.text

						if (setDataFunction && data && name) {
							setDataFunction({
								...data,
								[name]: formattedAddress,
							})
						}

						if (onPlaceSelected) {
							const result = {
								formattedAddress: formattedAddress,
								addressComponents: placeData.addressComponents,
								placeId: placeData.id,
								parsed: parseAddressComponents(
									placeData.addressComponents
								),
							}
							onPlaceSelected(result)
						}

						setShowSuggestions(false)
						setIsLoading(false)
						lastSearchValue.current = formattedAddress

						sessionTokenRef.current = Math.random()
							.toString(36)
							.substring(7)
					}
				} catch (error) {
					console.error('Error fetching place details:', error)
					if (isMounted.current) {
						setLoadError('Failed to load place details')
						setIsLoading(false)
					}
				}
			},
			[apiKey, setDataFunction, data, name, onPlaceSelected]
		)

		const handleSuggestionClick = useCallback(
			(suggestion) => {
				const placeId = suggestion.placePrediction?.placeId
				if (placeId) {
					fetchPlaceDetails(placeId)
				}
			},
			[fetchPlaceDetails]
		)

		const customChangeFunction = useCallback(() => {
			handleInputChange(value)
		}, [value, handleInputChange])

		const LocationIcon = () => (
			<svg
				className="address-suggestion-icon"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
			>
				<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
				<circle cx="12" cy="10" r="3"></circle>
			</svg>
		)

		return (
			<div
				ref={containerRef}
				style={{ position: 'relative', width: '100%' }}
			>
				{showLabel && <label htmlFor={placeholder}>{placeholder}</label>}
				<InputCustom
					name={name}
					type="text"
					placeholder={placeholder}
					value={value}
					setDataFunction={setDataFunction}
					data={data}
					autoFocus={autoFocus}
					required={required}
					disabled={disabled}
					data_cy={data_cy}
					className={className}
					label={label}
					autoComplete="off"
					changeFunction={customChangeFunction}
					{...props}
				/>

				{loadError && (
					<Box
						sx={{
							position: 'absolute',
							right: '12px',
							top: label ? '60%' : '50%',
							transform: 'translateY(-50%)',
							fontSize: '16px',
							color: '#ef4444',
							cursor: 'help',
							zIndex: 1,
						}}
						title={loadError}
					>
						⚠️
					</Box>
				)}

				{showSuggestions && (
					<div className="address-suggestions">
						{isLoading ? (
							<div className="loading-suggestions">
								<span>Searching</span>
								<div className="dot"></div>
								<div className="dot"></div>
								<div className="dot"></div>
							</div>
						) : suggestions.length > 0 ? (
							suggestions.map((suggestion, index) => {
								const prediction = suggestion.placePrediction
								if (!prediction) return null

								return (
									<div
										key={index}
										className="address-suggestion-item"
										onClick={() =>
											handleSuggestionClick(suggestion)
										}
									>
										<LocationIcon />
										<div className="address-suggestion-content">
											<div className="address-suggestion-main">
												{prediction.structuredFormat
													?.mainText?.text ||
													prediction.text?.text}
											</div>
											{prediction.structuredFormat
												?.secondaryText?.text && (
												<div className="address-suggestion-secondary">
													{
														prediction
															.structuredFormat
															.secondaryText.text
													}
												</div>
											)}
										</div>
									</div>
								)
							})
						) : value && value.length >= minChars ? (
							<div className="no-suggestions">
								No addresses found
							</div>
						) : null}
					</div>
				)}
			</div>
		)
	}
)

GoogleAddressInput.displayName = 'GoogleAddressInput'
export default GoogleAddressInput
